// Repositorio de configuración de overtime (T60). Envuelve `apiClient` sobre las rutas v1 de
// festivos/horario/tasas. Porta SOLO los 7 métodos de configuración (plan.md §0.2): getRateTypes,
// getHolidays, createHoliday, updateHoliday, deleteHoliday, getWorkSchedule, upsertWorkSchedule.
// NO porta los 8 métodos del motor de cálculo (calculateForActivity, getByActivity, getByUser,
// getByUserWithStandby, getSummary, createEntry, deleteEntry, exportByActivity) — fuera de alcance,
// nacen en `change-mgmt`/`iam` cuando migren. `overtime.api.js` sobrevive por esos consumidores.
//
// Rutas confirmadas (specs/api/v1-endpoints.txt, core-manage-api-v1.json):
// - `/overtime/holidays` → `/country-holidays` (GET/POST/DELETE — v1-endpoints.txt:31,158,391).
//   **NO hay PUT** — `updateHoliday` queda documentado como no soportado (mismo hueco que G-8).
//   **Hueco nuevo, no documentado previamente (ver improvements.md G-34/G-35)**: `CountryHolidayDto`
//   (core-manage-api-v1.json:3670) NO trae `country_id`/`countryId`, solo `countryCode` (requerido,
//   siempre presente — ya no ".nullish() solo en el listado" como en legado), y el GET solo declara
//   `search`/`page`/`pageSize` como parámetros (sin `country_id`/`year`). Mitigación aplicada:
//   `Holiday.countryId` (constitution.md no permite tocar `domain/` en esta tarea) se rellena con
//   `countryCode`; el filtro por país/año se hace en cliente sobre una página grande (mismo patrón
//   que G-20/G-23), comparando contra `countryCode` — solo funciona si quien llama pasa el CODE, no
//   el id numérico real de `/countries` (si no calza, el resultado es lista vacía, no una mezcla de
//   países). El POST envía `countryCode` (calcado del shape de lectura, precedente G-10/G-21) usando
//   el valor de `form.countryId` tal cual — **sin confirmar con backend si el payload espera
//   countryCode o un id**, ver reporte final de T60.
// - `/overtime/work-schedules/{clientId}` → `GET/PUT /clients/{clientId}/work-schedule`
//   (`WorkScheduleDto`, camelCase, schema completo en core-manage-api-v1.json:12548). Los campos
//   nuevos `clientId`/`isDefault`/`timezonePolicy` no tienen consumidor en `application/` y se
//   ignoran aquí.
// - `/overtime/rate-types` sin cambio de ruta (v1-endpoints.txt:219) pero sin schema documentado
//   (`JsonOk` genérico) — se asume camelCase, mismo precedente que T58
//   (`change-mgmt/infrastructure/shared/overtime.bridge.ts`).

import { z } from 'zod';
import apiClient from '@shared/api/http';
import type {
  Holiday,
  HolidayFormValues,
  RateType,
  WorkSchedule,
  WorkScheduleFormValues,
} from '../../domain/overtime/overtime.types';
import type { ClientId, CountryId, HolidayId, RateTypeId } from '../../domain/ids';
import type { Weekday } from '../../domain/shared.types';

const IdSchema = z.union([z.string(), z.number()]).transform(String);

const HolidayDtoSchema = z.object({
  id: IdSchema,
  countryCode: z.string(),
  name: z.string(),
  holidayDate: z.string(),
  isRecurring: z.boolean(),
});

const WorkScheduleDtoSchema = z.object({
  timezone: z.string().nullish(),
  workdayStart: z.string().nullish(), // 'HH:mm:ss' al leer
  workdayEnd: z.string().nullish(),
  lunchStart: z.string().nullish(),
  lunchEnd: z.string().nullish(),
  workDays: z.array(z.number()).nullish(),
  weeklyHours: z.number().nullish(),
});

const RateTypeDtoSchema = z.object({
  id: IdSchema,
  code: z.string(),
  nameEs: z.string(),
  surchargePct: z.number(),
  factor: z.number(),
  isHoliday: z.boolean(),
  isNocturnal: z.boolean(),
  isExtra: z.boolean(),
});

function holidayToDomain(dto: z.infer<typeof HolidayDtoSchema>): Holiday {
  return {
    id: dto.id as HolidayId,
    countryId: dto.countryCode as CountryId, // mitigación: v1 no expone id de país, ver cabecera
    countryCode: dto.countryCode,
    name: dto.name,
    holidayDate: dto.holidayDate,
    isRecurring: dto.isRecurring,
  };
}

function holidayToDto(form: HolidayFormValues) {
  return {
    countryCode: form.countryId, // sin confirmar con backend, ver cabecera
    name: form.name,
    holidayDate: form.holidayDate,
    isRecurring: form.isRecurring,
  };
}

// El recorte 'HH:mm:ss'→'HH:mm' NO ocurre aquí: toDomain preserva los tiempos crudos. El recorte +
// defaults es una regla de dominio (workSchedule.rules.ts toWorkScheduleForm), no del mapper.
function workScheduleToDomain(dto: z.infer<typeof WorkScheduleDtoSchema>): WorkSchedule {
  return {
    timezone: dto.timezone ?? '',
    workdayStart: dto.workdayStart ?? '',
    workdayEnd: dto.workdayEnd ?? '',
    lunchStart: dto.lunchStart ?? '',
    lunchEnd: dto.lunchEnd ?? '',
    workDays: (dto.workDays ?? []) as Weekday[],
    weeklyHours: dto.weeklyHours ?? 0,
  };
}

function workScheduleToDto(form: WorkScheduleFormValues) {
  return {
    timezone: form.timezone,
    workdayStart: form.workdayStart,
    workdayEnd: form.workdayEnd,
    lunchStart: form.lunchStart,
    lunchEnd: form.lunchEnd,
    workDays: form.workDays,
    weeklyHours: form.weeklyHours,
  };
}

function rateTypeToDomain(dto: z.infer<typeof RateTypeDtoSchema>): RateType {
  return {
    id: dto.id as RateTypeId,
    code: dto.code,
    nameEs: dto.nameEs,
    surchargePct: dto.surchargePct,
    factor: dto.factor,
    isHoliday: dto.isHoliday,
    isNocturnal: dto.isNocturnal,
    isExtra: dto.isExtra,
  };
}

export const overtimeRepository = {
  async getRateTypes(): Promise<RateType[]> {
    const { data } = await apiClient.get('/overtime/rate-types');
    return z
      .array(RateTypeDtoSchema)
      .parse(data ?? [])
      .map(rateTypeToDomain);
  },

  // G-34/G-35: `/country-holidays` solo documenta `search`/`page`/`pageSize` (sin `country_id`/
  // `year`) y `CountryHolidayDto` no trae id de país — se trae una página grande (mismo patrón que
  // G-20/G-23) y se filtra en cliente por año (siempre correcto, deriva de `holidayDate`) y por país
  // (correcto solo si `params.countryId` ya es un `countryCode`, ver cabecera del archivo).
  async getHolidays(params: { countryId?: CountryId; year?: number } = {}): Promise<Holiday[]> {
    const { data } = await apiClient.get('/country-holidays', { params: { pageSize: 200 } });
    const holidays = z
      .array(HolidayDtoSchema)
      .parse(data ?? [])
      .map(holidayToDomain);
    return holidays.filter((h) => {
      const matchesCountry = !params.countryId || h.countryId === params.countryId;
      const matchesYear = !params.year || h.holidayDate.startsWith(String(params.year));
      return matchesCountry && matchesYear;
    });
  },

  async createHoliday(data: HolidayFormValues): Promise<void> {
    await apiClient.post('/country-holidays', holidayToDto(data));
  },

  // G-8: `/country-holidays` solo tiene GET/POST/DELETE en v1, no hay PUT — sin sustituto.
  async updateHoliday(_id: HolidayId, _data: HolidayFormValues): Promise<void> {
    throw new Error('Updating holidays is not available in Core Manage API v1 (G-8)');
  },

  async deleteHoliday(id: HolidayId): Promise<void> {
    await apiClient.delete(`/country-holidays/${id}`);
  },

  async getWorkSchedule(clientId: ClientId): Promise<WorkSchedule | null> {
    const { data } = await apiClient.get(`/clients/${clientId}/work-schedule`);
    if (!data) return null;
    return workScheduleToDomain(WorkScheduleDtoSchema.parse(data));
  },

  async upsertWorkSchedule(clientId: ClientId, form: WorkScheduleFormValues): Promise<void> {
    await apiClient.put(`/clients/${clientId}/work-schedule`, workScheduleToDto(form));
  },
};

